import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Button
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import ShieldIcon from '@mui/icons-material/Shield';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import GppGoodIcon from '@mui/icons-material/GppGood';

const services = [
  'Network security assessment',
  'Firewall configuration',
  'Intrusion detection setup',
  'VPN implementation',
  'Network monitoring',
  'Security policy setup',
  'Access control configuration',
  'Security patch management',
  'Network encryption',
  'Security audit and reporting'
];

const benefits = [
  {
    icon: SecurityIcon,
    title: 'Expert Security',
    description: 'Certified security specialists with proven expertise'
  },
  {
    icon: ShieldIcon,
    title: 'Proactive Protection',
    description: '24/7 monitoring and threat prevention'
  },
  {
    icon: VpnLockIcon,
    title: 'Secure Access',
    description: 'Enterprise-grade VPN and encryption'
  },
  {
    icon: GppGoodIcon,
    title: 'Compliance Ready',
    description: 'Meet industry security standards'
  }
];

const securityFeatures = [
  'Firewall Protection',
  'Intrusion Prevention',
  'Data Encryption',
  'Access Control',
  'Network Monitoring',
  'Threat Detection',
  'VPN Services',
  'Security Updates',
  'Policy Enforcement',
  'Incident Response'
];

function NetworkSecurity() {
  return (
    <Box sx={{ py: 6, bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            className="gradient-text"
            sx={{ mb: 3 }}
          >
            Network Security Services
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: 'text.secondary', mb: 4 }}
          >
            Comprehensive network protection and security solutions
          </Typography>
          <Button
            component={RouterLink}
            to="/booking"
            variant="contained"
            size="large"
            sx={{
              py: 2,
              px: 4,
              fontSize: '1.2rem',
              background: 'linear-gradient(45deg, #dc2626 30%, #ef4444 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #ef4444 30%, #dc2626 90%)',
              }
            }}
          >
            Schedule Security Audit
          </Button>
        </Box>

        {/* Benefits Section */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card className="glass-effect card-hover" sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box sx={{ mb: 2, color: 'error.main' }}>
                    {React.createElement(benefit.icon, { sx: { fontSize: 40 } })}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Services List */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            mb: 8,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            Our Security Services
          </Typography>
          <Grid container spacing={2}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="error" />
                    </ListItemIcon>
                    <ListItemText primary={service} />
                  </ListItem>
                </List>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Security Features Section */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            Security Features
          </Typography>
          <Grid container spacing={2}>
            {securityFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card 
                  className="glass-effect"
                  sx={{ 
                    textAlign: 'center',
                    py: 2
                  }}
                >
                  <Typography variant="h6">
                    {feature}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

export default NetworkSecurity;
